import { DocumentServicesObject } from '@wix/document-services-types';
import {
  createEditorSuperAppsHandlers,
  EditorSuperAppsHandlers,
} from './editorSuperAppsHandlers';
import {
  createEditorTPAHandlers,
  EditorTPAHandlers,
} from './editorTPAHandlers/editorTPAHandlers';
import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import {
  createEditorPublicDataHandlers,
  EditorPublicDataHandlers,
} from './editorPublicDataHandlers/editorPublicDataHandlers';
import {
  createEditorTPAPreviewHandlers,
  EditorTPAPreviewHandlers,
} from './editorTPAPreviewHandlers';
import {
  createEditorUILibHandlers,
  EditorUILibHandlers,
} from './editorUILibHandlers/editorUILibHandlers';
import {
  AppRefreshService,
  createTpaHelpService,
  createTpaMediaDialogService,
  createTpaSettingsPanelService,
  createTpaStyleService,
} from '../services';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';

export type EditorEventHandlers = EditorSuperAppsHandlers &
  EditorTPAHandlers &
  EditorPublicDataHandlers &
  EditorTPAPreviewHandlers &
  EditorUILibHandlers;

export type EditorEventHandlerKey = keyof EditorEventHandlers;

export class EditorEventHandlersFactory {
  private editorSuperAppsHandlers: EditorSuperAppsHandlers;
  private editorTPAHandlers: EditorTPAHandlers;
  private editorPublicDataHandlers: EditorPublicDataHandlers;
  private editorTPAPreviewHandlers: EditorTPAPreviewHandlers;
  private editorUILibHandlers: EditorUILibHandlers;

  constructor(
    platformApiMethods: PlatformApiMethods,
    documentServicesAPI: DocumentServicesObject,
    experimentsAPI: IExperimentsApiAdapter,
    appRefreshService: AppRefreshService,
  ) {
    const tpaMediaDialogService = createTpaMediaDialogService(
      platformApiMethods,
      documentServicesAPI,
    );
    const tpaStyleService = createTpaStyleService(platformApiMethods);
    const tpaHelpService = createTpaHelpService(
      platformApiMethods,
      documentServicesAPI,
    );
    const tpaSettingsPanelService = createTpaSettingsPanelService(
      platformApiMethods,
      documentServicesAPI,
      { tpaHelpService },
    );
    this.editorTPAHandlers = createEditorTPAHandlers(
      platformApiMethods,
      documentServicesAPI,
      appRefreshService,
      { tpaSettingsPanelService, tpaMediaDialogService, tpaStyleService },
    );
    this.editorSuperAppsHandlers = createEditorSuperAppsHandlers(
      platformApiMethods,
      documentServicesAPI,
      { tpaMediaDialogService, tpaHelpService },
    );
    this.editorPublicDataHandlers = createEditorPublicDataHandlers(
      platformApiMethods,
      documentServicesAPI,
    );
    this.editorTPAPreviewHandlers = createEditorTPAPreviewHandlers(
      platformApiMethods,
      documentServicesAPI,
      { tpaSettingsPanelService, tpaHelpService },
      {
        isApplicationInstalled: this.editorTPAHandlers.isApplicationInstalled,
        isFullWidth: this.editorTPAHandlers.isFullWidth,
      },
    );
    this.editorUILibHandlers = createEditorUILibHandlers(
      platformApiMethods,
      documentServicesAPI,
      experimentsAPI,
      { tpaStyleService },
    );
  }

  get eventHandlers(): EditorEventHandlers {
    return {
      ...this.editorSuperAppsHandlers,
      ...this.editorTPAHandlers,
      ...this.editorPublicDataHandlers,
      ...this.editorTPAPreviewHandlers,
      ...this.editorUILibHandlers,
    };
  }
}
